import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  MenuOptionValue,
  NestedPick,
  Paginated,
  PaginationQuery,
  PaginationQueryWithFields,
  Version,
  Versions,
} from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { Claim, ClaimNoCarDependency, ClaimStats, ClaimVersion, UpsertClaim } from "./types";

export const useAdminClaimsApi = () => {
  const api = useApi();

  const getAdminClaim = useCallback(
    (claimId: string, signal: AbortSignal) => {
      const url = `/admin/claims/${claimId}`;
      return useSWRMutation<Claim | null, CustomError, string>(url, (url) => api.get<Claim>({ url, signal }));
    },
    [api],
  );

  const useAdminClaims = useCallback(
    <K extends keyof ClaimNoCarDependency>(
      query: PaginationQueryWithFields<NestedPick<ClaimNoCarDependency, K>>,
      signal: AbortSignal,
      carId?: string,
    ) => {
      const url = buildUrl<NestedPick<ClaimNoCarDependency, K>>(
        carId ? `/admin/cars/${carId}/claims` : "/admin/claims",
        query,
      );
      return useSWR<Paginated<NestedPick<ClaimNoCarDependency, K>> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<NestedPick<ClaimNoCarDependency, K>>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminClaim = useCallback(
    async (body: UpsertClaim): Promise<{ id: string }> => {
      const url = `/admin/claims`;
      const id = await api.post<UpsertClaim, { id: string }>(url, body);
      api.mutateCache([url, `/admin/cars/${body.carId}/claims`]);
      return id;
    },
    [api],
  );

  const updateAdminClaim = useCallback(
    async (id: string, body: UpsertClaim) => {
      const url = `/admin/claims/${id}`;
      await api.put<UpsertClaim>(url, body);
      api.mutateCache([`/admin/claims`, `/admin/cars/${body.carId}/claims`, `/admin/claims/${id}/versions`]);
    },
    [api],
  );

  const deleteAdminClaim = useCallback(
    async (claimId: string, carId: string): Promise<void> => {
      const url = `/admin/claims/${claimId}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/claims`, `/admin/cars/${carId}/claims`]);
      });
    },
    [api],
  );

  const getAdminClaimHistory = useCallback(
    (query: PaginationQuery, claimID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/claims/${claimID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminClaimVersion = useCallback(
    (claimId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/claims/${claimId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<ClaimVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<ClaimVersion>>({ url, signal }),
      );
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/claims/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  const importClaims = useCallback(
    async (importID: string, type: string) => {
      const url = `/admin/claims/${importID}/import/${type}`;
      await api.postNoResult(url);
      api.mutateCache([url, `/admin/claims_imports/${type}/${importID}`, `/admin/claims`]);
    },
    [api],
  );

  const useClaimStats = useCallback(
    (signal: AbortSignal) => {
      return useSWR<ClaimStats | null, CustomError, string>("/admin/claims/stats", (url) =>
        api.get<ClaimStats>({ url, signal }),
      );
    },
    [api],
  );

  return {
    deleteAdminClaim,
    updateAdminClaim,
    insertAdminClaim,
    useAdminClaims,
    getAdminClaim,
    getAdminClaimHistory,
    getAdminClaimVersion,
    getFieldFilters,
    importClaims,
    useClaimStats,
  };
};
